import { useMutation, useQuery } from '@apollo/client'
import { BodyText, Button, Col, HeadingText, Row } from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge, isEmpty } from 'lodash-es'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import InfoText from 'ppbl/components/InfoText'
import { updateApplicationCachedData } from 'ppbl/graphql/__utils__'
import { updateApplicationMutation } from 'ppbl/graphql/mutations'
import {
  getCreditApplicationDataQuery,
  getIndustriesByProductIdentifier,
} from 'ppbl/graphql/queries'
import { useApplicationContext } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import ReadOnlyCard from 'ppbl/components/ReadOnlyCard'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import serverData from 'ppbl/utils/server-data'
import { santizeInputFields } from 'ppbl/utils/string-helper'
import { scrollOnErrors } from 'ppbl/utils/validation-helper'
import React, { useEffect, useState } from 'react'
import { Field, Form, FormSpy } from 'react-final-form'
import {
  scrollToTopOnContinue,
  getProductIdentifier,
  getApplicationFieldsData,
  updateApplicationPayloadGeneratorPage4,
  handleGraphqlError,
} from 'ppbl/screens/__utils__'
import {
  applicationPage4Fields,
  applicationPage4FieldsRenewal,
  getApplicationFields,
} from '../applicationFieldMapper'
import './styles.css'
import { cloneAndMask } from 'ppbl/utils/data-masker'
import createDecorator from 'ppbl/utils/form/decorator'
import { businessEntityTypeDecoder, applicationStateList } from '../constants'
import PreApprovalBanner from 'ppbl/components/PreApprovalBanner'

type Props = {
  applicationType: string
  creditApplicationLoading: boolean
}

const fptiData = {
  page: 'applicationPage4',
  event_name: FPTI_EVENTS.PAGE.PAGE4,
}

const ApplicationPage4: React.FC<Props> = ({
  applicationType,
  creditApplicationLoading,
}) => {
  const { data, updateApplicationContext } = useApplicationContext()

  useEffect(() => {
    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    clientLogger.info(
      'Application Page 4 is loaded - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
  }, [])

  const { newApplicationPage4Fields } = getApplicationFields(applicationType)

  const isPaypalApplication = applicationType === 'paypal'

  const isRenewalApplication =
    applicationType === 'app-renewal' || applicationType === 'paypal-renewal'

  const renewalToken = serverData.startParams?.renewalToken

  const [industriesIntialised, setIndustriesIntialised] = useState(false)

  const { data: cachedApplicationData } = useQuery(
    getCreditApplicationDataQuery,
  )

  let industriesToSubIndustriesMapping =
    data?.applicationMetaData?.industriesToSubIndustriesMapping || {}

  const getSubIndustry = () => {
    if (
      !isEmpty(industriesToSubIndustriesMapping) &&
      !isEmpty(cachedApplicationData)
    ) {
      let subIndustry = industriesToSubIndustriesMapping[
        cachedApplicationData.businessIndustry
      ]?.filter((subInd) => {
        return cachedApplicationData.businessSubIndustry === subInd?.value
      })
      let subIndustryPrimaryText =
        subIndustry.length > 0 ? subIndustry[0]?.primaryText : null
      return subIndustryPrimaryText
    }
    return null
  }

  const shouldChangeApplicationFields = () => {
    if (!isRenewalApplication) return false

    return (
      cachedApplicationData.businessEntityType === null ||
      cachedApplicationData.businessStartDate === null ||
      (['CORPORATION', 'LIMITED_LIABILITY_COMPANY', 'PARTNERSHIP'].includes(
        cachedApplicationData.businessEntityType,
      ) &&
        cachedApplicationData.stateOfIncorporation === null) ||
      cachedApplicationData.businessIndustry === null ||
      !getSubIndustry()
    )
  }

  const [changeApplicationFields, setChangeApplicationFields] = useState(
    shouldChangeApplicationFields(),
  )

  const [applicationPageFields, setApplicationPageFields] = useState(
    isRenewalApplication
      ? (changeApplicationFields && applicationPage4FieldsRenewal) ||
          newApplicationPage4Fields
      : applicationPage4Fields,
  )

  const mapIndustriesToSubIndustries = (industriesListData) => {
    const {
      credit: {
        businessCredit: {
          industries: { industries },
        },
      },
    } = industriesListData
    industries.forEach((industry) => {
      industriesToSubIndustriesMapping[industry.name] = industry.subIndustries
        .map((subIndustry) => {
          if (
            !!cachedApplicationData.businessSubIndustry &&
            cachedApplicationData.businessSubIndustry === subIndustry.code
          ) {
            updateApplicationCachedData(
              { businessIndustry: industry.name },
              cachedApplicationData,
            )
          }

          return {
            value: subIndustry.code,
            primaryText: subIndustry.name,
          }
        })
        .sort((a, b) => a.primaryText.localeCompare(b.primaryText))
    })

    data.applicationMetaData.industriesToSubIndustriesMapping =
      industriesToSubIndustriesMapping
  }

  const { data: industriesListData } = useQuery(
    getIndustriesByProductIdentifier,
    {
      variables: {
        productIdentifier: getProductIdentifier(serverData.startParams.product),
      },
      onError(getIndustriesError) {
        handleGraphqlError(
          getIndustriesError,
          'Error encountered while fetching industries list details in application page 4',
          'ApplicationPage 4 - getIndustriesByProductIdentifier',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
      onCompleted(response) {
        mapIndustriesToSubIndustries(response)
        setIndustriesIntialised(true)
      },
    },
  )

  const [updateApplication, { loading }] = useMutation(
    updateApplicationMutation,
    {
      onCompleted(response) {
        clientLogger.info(
          'Response received after updating business details in application page 4',
          cloneAndMask(response),
        )
        // push the response.data.credit.businessCredit.createApplication.creditApplication into context
        sessionStorage.setItem('mostRecentCompletedPage', 'applicationPage4')
        updateApplicationContext({
          applicationMetaData: {
            ...data.applicationMetaData,
            mostRecentCompletedPage: 'applicationPage4',
          },
        })
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/applicationPage5`,
        )
      },
      onError(err) {
        handleGraphqlError(
          err,
          'Error encountered while updating business details in application page 4, redirecting to error page',
          'ApplicationPage 4 - updateApplicationMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    },
  )

  const handleFieldsOnChange = () => {
    setChangeApplicationFields(true)
    setApplicationPageFields(applicationPage4FieldsRenewal)
  }

  const handleApplicationSubmit = async (values: any) => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage4_continue_button',
        link: 'applicationPage4_continue_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.PAGE_CONTINUE.PAGE4,
      }),
    )

    const applicationValues = getApplicationFieldsData(
      isRenewalApplication
        ? applicationPage4FieldsRenewal
        : applicationPage4Fields,
      cachedApplicationData,
      values,
    )

    clientLogger.info(
      'application page 4 submitted - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    clientLogger.info(
      'Legal bussiness name for the application',
      data?.applicationMetaData?.legalBusinessName,
    )
    if (isPaypalApplication) {
      clientLogger.info(
        'paypal users encrypted account number',
        serverData?.user?.encryptedAccountNumber,
      )
    } else if (renewalToken) {
      clientLogger.info('renewal user renewal token', renewalToken)
    }

    scrollToTopOnContinue()
    //sanitize the input values to prevent XSS
    santizeInputFields(applicationValues)

    updateApplicationCachedData(applicationValues, cachedApplicationData)
    updateApplication({
      variables: {
        updateInput: updateApplicationPayloadGeneratorPage4(
          applicationValues,
          cachedApplicationData?.legalBusinessName,
          data.applicationMetaData.applicationId,
        ),
      },
    })
  }

  const constructBusinessEntityField = () => {
    return {
      'Business entity type':
        businessEntityTypeDecoder[cachedApplicationData.businessEntityType],
    }
  }

  const constructDoingBusinessAsField = () => {
    return {
      'DBA or trade name': cachedApplicationData.doingBusinessAs,
    }
  }

  const constructFields = () => {
    const businessSubIndustryText = getSubIndustry()
    const showBussinessIncorporationRegion = [
      'CORPORATION',
      'LIMITED_LIABILITY_COMPANY',
      'PARTNERSHIP',
    ].includes(cachedApplicationData.businessEntityType)

    const cardFields = {
      ...(!!cachedApplicationData.doingBusinessAs && {
        'DBA or trade name': cachedApplicationData.doingBusinessAs,
      }),
      'Business entity type':
        businessEntityTypeDecoder[cachedApplicationData.businessEntityType],
      'Business start date': cachedApplicationData.businessStartDate,
      ...(showBussinessIncorporationRegion && {
        'State of incorporation': cachedApplicationData.stateOfIncorporation,
      }),
      'Business industry': cachedApplicationData.businessIndustry,
      'Business sub-industry': businessSubIndustryText,
    }

    return cardFields
  }

  const getIndustriesList = () => {
    const {
      credit: {
        businessCredit: {
          industries: { industries },
        },
      },
    } = industriesListData
    return industries.map((industry) => {
      return {
        value: industry.name,
        primaryText: industry.name,
      }
    })
  }

  const focusOnErrors = createDecorator()

  return (
    <>
      {!industriesIntialised || loading || creditApplicationLoading ? (
        <div className="spinner">
          <SpinnerAdapter />
        </div>
      ) : (
        <>
          {data?.eligibilityData?.preApproved && (
            <PreApprovalBanner
              approvalCategory={data?.eligibilityData?.category}
              expiryDate={data?.eligibilityData?.expiryDate}
            />
          )}
        <Form onSubmit={handleApplicationSubmit} decorators={[focusOnErrors]}>
          {({ handleSubmit, hasValidationErrors }) => (
            <div className="applicationFormContainer">
              <form onSubmit={handleSubmit}>
                <div className="applicationTextSection">
                  <Row>
                    <Col form="full">
                      <HeadingText size="sm">
                        {getLocalizedText(
                          'application.applicationPages.applicationPage4.headingText',
                        )}
                      </HeadingText>
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <BodyText>
                        {getLocalizedText(
                          'application.applicationPages.applicationPage4.checkEligibilityText',
                        )}
                      </BodyText>
                    </Col>
                  </Row>
                </div>
                <div className="applicationFields">
                  <Row>
                    <Col form="full">
                      <BodyText strong data-dd-privacy="mask">
                        {cachedApplicationData?.legalBusinessName}
                      </BodyText>
                    </Col>
                  </Row>
                  {isRenewalApplication && !changeApplicationFields && (
                    <ReadOnlyCard
                      id="applicationPage4Card"
                      fieldsList={constructFields()}
                      isEditable
                      handleButtonClick={handleFieldsOnChange}
                    />
                  )}
                  {applicationPageFields.map((applicationField) => {
                    const { name, ...props } = applicationField
                    return (
                      <Row key={name}>
                        <Col form="full">
                          {{
                            businessEntityType: isRenewalApplication ? (
                              <ReadOnlyCard
                                id="applicationPage4Card"
                                fieldsList={constructBusinessEntityField()}
                                isEditable={false}
                                cardClassName="editableCardSingleField"
                              />
                            ) : (
                              <Field
                                name={name}
                                {...props}
                                initialValue={
                                  cachedApplicationData?.[name] || null
                                }
                              />
                            ),
                            businessSubIndustry: (
                              <FormSpy subscription={{ values: true }}>
                                {({ values: { businessIndustry } }) => (
                                  <Field
                                    name={name}
                                    {...props}
                                    initialValue={
                                      cachedApplicationData?.[name] || null
                                    }
                                    {...(!(
                                      Object.keys(
                                        industriesToSubIndustriesMapping,
                                      ).length === 0
                                    ) && {
                                      options:
                                        industriesToSubIndustriesMapping[
                                          businessIndustry
                                        ],
                                    })}
                                  />
                                )}
                              </FormSpy>
                            ),
                            doingBusinessAs: (isRenewalApplication &&
                              !!cachedApplicationData.doingBusinessAs && (
                                <ReadOnlyCard
                                  id="applicationPage4Card"
                                  fieldsList={constructDoingBusinessAsField()}
                                  isEditable={false}
                                  cardClassName="editableCardSingleField"
                                />
                              )) ||
                              (isRenewalApplication &&
                                !cachedApplicationData.doingBusinessAs &&
                                null) || (
                                <FormSpy subscription={{ values: true }}>
                                  {({ values: { doingBusinessAsRadio } }) =>
                                    doingBusinessAsRadio === 'yes' && (
                                      <Field
                                        name={name}
                                        {...props}
                                        initialValue={
                                          cachedApplicationData?.[name] || null
                                        }
                                      />
                                    )
                                  }
                                </FormSpy>
                              ),
                            doingBusinessAsRadio: (
                              <Field
                                name={name}
                                {...props}
                                initialValue={
                                  cachedApplicationData?.[name] ||
                                  (!!cachedApplicationData.doingBusinessAs &&
                                    'yes') ||
                                  'no'
                                }
                              />
                            ),
                            stateOfIncorporation: (
                              <FormSpy subscription={{ values: true }}>
                                {({ values: { businessEntityType } }) =>
                                  [
                                    'CORPORATION',
                                    'LIMITED_LIABILITY_COMPANY',
                                    'PARTNERSHIP',
                                  ].includes(
                                    businessEntityType ||
                                      cachedApplicationData.businessEntityType,
                                  ) && (
                                    <Field
                                      name={name}
                                      {...props}
                                      initialValue={
                                        cachedApplicationData?.[name] ||
                                        cachedApplicationData.stateOfIncorporation ||
                                        applicationStateList.find(
                                          (item) =>
                                            item.value ===
                                            cachedApplicationData.businessState,
                                        )?.value ||
                                        null
                                      }
                                    />
                                  )
                                }
                              </FormSpy>
                            ),
                          }[name] || (
                            <Field
                              name={name}
                              {...props}
                              initialValue={
                                cachedApplicationData?.[name] || null
                              }
                              {...(name === 'businessIndustry' && {
                                options: getIndustriesList().sort((a, b) =>
                                  a.primaryText.localeCompare(b.primaryText),
                                ),
                              })}
                            />
                          )}
                        </Col>
                      </Row>
                    )
                  })}
                </div>
                <Button
                  className="ctaSection"
                  size="lg"
                  onClick={() => scrollOnErrors(hasValidationErrors)}
                  type="submit"
                >
                  {getLocalizedText(
                    'application.applicationPages.applicationPage4.continueButtonText',
                  )}
                </Button>
              </form>
              <InfoText />
            </div>
          )}
        </Form>
        </>
      )}
    </>
  )
}

export default ApplicationPage4
